const KST = 'Asia/Seoul';

/** A stamp with no zone of its own is UTC — the servers send both shapes and neither means browser-local. */
const HAS_TIME_ZONE = /(Z|[+-]\d{2}:?\d{2})$/;

/**
 * The ledger has sent its instants in `Date.toString()` form ("Mon Oct 05 2026 13:33:05 GMT+0000 (…)"),
 * whose zone sits mid-string; suffixing a Z onto it would leave the reading to the browser's leniency.
 */
const NAMES_GMT_OFFSET = /GMT[+-]\d{4}/;

const instantOf = (iso?: string | null): Date | null => {
  if (!iso) return null;
  const at = new Date(HAS_TIME_ZONE.test(iso) || NAMES_GMT_OFFSET.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(at.getTime()) ? null : at;
};

/** Epoch milliseconds under the same reading the KST renderers use, so an order and a rendered time agree. */
export const instantMs = (iso?: string | null): number | null => instantOf(iso)?.getTime() ?? null;

const inKst = (at: Date, options: Intl.DateTimeFormatOptions): string =>
  new Intl.DateTimeFormat('sv-SE', { timeZone: KST, hour12: false, ...options }).format(at).replace(',', '');

/** The suffix every full stamp carries: a rendered time nobody can place is a time nobody can act on [G-16]. */
const ZONE = 'KST';

/** [G-16]: one clock for every screen — a rendered time is stamped KST, never left browser-local. */
export const kstTimestamp = (iso?: string | null): string => {
  const at = instantOf(iso);
  if (!at) return '—';
  return `${inKst(at, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })} ${ZONE}`;
};

/** The same clock without the seconds, for screens that find a record by the day and hour it turned. */
export const kstMinute = (iso?: string | null): string => {
  const at = instantOf(iso);
  if (!at) return '—';
  return `${inKst(at, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })} ${ZONE}`;
};

/** Wall-clock only, for a scan log an operator reads against the clock on the warehouse wall. */
export const kstClock = (iso?: string | null): string => {
  const at = instantOf(iso);
  if (!at) return '';
  return inKst(at, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

/** `HH:mm` on the KST clock, for a notice that says when something started today. */
export const kstHourMinute = (iso?: string | null): string => {
  const at = instantOf(iso);
  if (!at) {
    return '—';
  }
  return inKst(at, { hour: '2-digit', minute: '2-digit' });
};

/** `07-09 11:32` — the warehouse log finds a row by the day and hour, never by the year. */
export const kstLogStamp = (iso?: string | null): string | null => {
  const at = instantOf(iso);
  if (!at) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: KST,
    hour12: false,
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(at);
  const read = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
  return `${read('month')}-${read('day')} ${read('hour')}:${read('minute')}`;
};

/** The KST calendar day a record belongs to; a UTC evening already belongs to the next one. */
export const kstDay = (iso?: string | null): string => {
  const at = instantOf(iso);
  if (!at) return '—';
  return inKst(at, { year: 'numeric', month: '2-digit', day: '2-digit' });
};

/**
 * `2026/08/14 23:18` on the warehouse clock, for paper that prints no zone of its own: an unmarked time
 * on a warehouse-floor sheet is KST by definition [G-16], so it is converted here rather than copied.
 */
export const kstLabelStamp = (iso?: string | null): string => {
  const at = instantOf(iso);
  if (!at) return '—';
  return inKst(at, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(
    /-/g,
    '/',
  );
};

/** `10/18 14:05 KST` — a badge names the day and minute an order changed, never the year (SSD-7450). */
export const kstMonthDayMinute = (iso?: string | null): string | null => {
  const stamp = kstLogStamp(iso);
  return stamp === null ? null : `${stamp.replace('-', '/')} ${ZONE}`;
};

/** A `datetime-local` value, read on the KST clock: `YYYY-MM-DDTHH:mm`, seconds optional. */
const KST_INPUT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * The instant an operator typed on the KST clock, as an ISO UTC string. The box is read as KST whatever the
 * browser's own zone is [G-16]; Korea keeps no daylight saving, so +09:00 is exact for every date.
 * Null when the box is empty or holds no real date (a 31 February would otherwise roll into March).
 */
export const kstInputToIso = (value: string): string | null => {
  const match = KST_INPUT.exec(value.trim());
  if (!match) {
    return null;
  }
  const [, year, month, day, hour, minute, second = '00'] = match;
  const at = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}+09:00`);
  if (Number.isNaN(at.getTime())) {
    return null;
  }
  // Round-trip the fields so an impossible day is refused rather than silently moved.
  const back = inKst(at, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  return back === `${year}-${month}-${day} ${hour}:${minute}` ? at.toISOString() : null;
};

/** `YYYY-MM-DDTHH:mm` on the KST clock, the shape a `datetime-local` box takes for its value or its `max`. */
export const kstInputValue = (epochMs: number): string =>
  inKst(new Date(epochMs), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).replace(' ', 'T');
